
import Vue from "vue";
import { Hub } from "aws-amplify/utils";
import { loginWithGoogleWorkspace } from "@/auth/client";
import { adminLogin, persistAdminApiJwt } from "@/api";
import { logger } from "@/utils/logger";
import {
  adminOAuthRedirectMessage,
  consumeAdminOAuthFailure
} from "@/auth/adminOAuthError";

type AuthErrorLike = {
  name?: string;
  message?: string;
};

export default Vue.extend({
  name: "login",
  data: () => ({
    loading: false,
    error: false,
    errorMessage: "Login failed. Please try again.",
    showAdminForm: false,
    adminUsername: "",
    adminPassword: "",
    adminLoading: false
  }),
  methods: {
    async handleGoogleLogin() {
      this.loading = true;
      this.error = false; // Reset error on new attempt

      try {
        logger.debug("Attempting Google SSO using GoogleWorkspace provider");
        await loginWithGoogleWorkspace();
      } catch (error) {
        const authError = error as AuthErrorLike;
        logger.error(
          "Error initiating Google SSO with GoogleWorkspace provider",
          authError as Error
        );

        this.errorMessage = authError?.message
          ? `Google Sign-In failed: ${authError.message}`
          : "Failed to initiate Google Sign-In. Please check console.";
        this.error = true;
        this.loading = false;
      }
    },
    async handleAdminLogin() {
      if (!this.adminUsername || !this.adminPassword) {
        this.errorMessage = "Please enter both username and password";
        this.error = true;
        return;
      }

      this.adminLoading = true;
      this.error = false;

      try {
        console.log("🔑 Login.vue: Attempting admin login...");

        const result = await adminLogin(this.adminUsername, this.adminPassword);

        console.log("🔑 Login.vue: Login successful!", result);

        if (!result?.token) {
          throw new Error("Login succeeded but no API token was returned.");
        }

        // Store the API admin JWT (Cognito must not overwrite this — see App.vue)
        persistAdminApiJwt(result.token);
        localStorage.setItem("user", JSON.stringify(result.admin));
        localStorage.setItem("adminId", result.admin.id.toString());

        console.log("🔑 Login.vue: Stored JWT and admin data in localStorage");

        // Emit login event and redirect
        this.$emit("logined");
        this.$router.push("/");
      } catch (error) {
        console.error("🔑 Login.vue: Admin login failed:", error);
        this.errorMessage =
          error instanceof Error
            ? error.message
            : "Login failed. Please try again.";
        this.error = true;
      } finally {
        this.adminLoading = false;
      }
    }
  },
  mounted() {
    // Listen for auth errors passed via redirect (optional, Hub listener is primary)
    const unsubscribe = Hub.listen("auth", ({ payload }) => {
      if (payload.event === "signInWithRedirect_failure") {
        console.error("Hub captured signInWithRedirect_failure:", payload.data);
        // Access the message property from the nested error object
        const authError = payload.data?.error as any; // Use 'as any' for simplicity or import AuthError type
        this.errorMessage =
          authError?.message || "An error occurred during Google Sign-In.";
        this.error = true;
        this.loading = false; // Stop loading on failure
      }
    });

    // Clean up listener when component is destroyed
    this.$once("hook:beforeDestroy", () => {
      unsubscribe();
    });

    // Optional: Check query params for error passed from redirect
    if (this.$route.query.error) {
      const persistedFailure = consumeAdminOAuthFailure();
      this.errorMessage = persistedFailure
        ? persistedFailure.message
        : adminOAuthRedirectMessage(this.$route.query.error);
      this.error = true;
    }
  }
});
